import { useMemo, useState, useEffect, useRef, useSyncExternalStore, useCallback } from 'react';
import { publishScope } from '@/views/handover/paletteStore';
import { newActe, onNewActe, clearNewActe } from '@/lib/actesNew';
import { onActeOpen, takeActeOpen } from '@/lib/actesOpen';
import { useQuery } from '@tanstack/react-query';
import type { Lang } from '@/data/types';
import { KBIcon } from '@/components/kbIcons';
import { friendlyError } from '@/lib/friendlyError';
import { actesDemandes, actesDemandesPage, actesSearch, actesDocumentCounts, actesCommentCounts, actesOne, actesUpdateDemande, type ActeRow, type ActesReference } from '@/integrations/actesApi';
import { ActeModal } from './ActeModal';
import { pillOf } from './pill';
import { OwnerPicker, peopleReader, nameOf } from './ActesPerson';
import { DocumentPicker } from './DocumentPicker';
import { ActeView } from './ActeView';
import { ScansList } from './ScansList';
import { fmtDay } from './dates';
import { countryOfRef, typeLabelEn, actesEn } from '@/lib/actesCountry';

/**
 * DEMANDES — the civil-record requests themselves.
 *
 * The table NEVER starts by loading everything: there are some 4 700, and the
 * old site's first version tried. It opens on yours, and the other scopes are
 * asked for on purpose. The search goes to the server, so it reads all of them
 * even though the table holds a few hundred.
 */
type Scope = 'mine' | 'all' | 'none';
/* The reception log belongs here too: standing in front of Demandes, what the
   team asks is "did that scan arrive, and did it land on a dossier". */
type View = 'table' | 'scans';

/* ── LES COLONNES ──
   Eleven columns is the right answer for nobody: the person chasing town halls
   wants the authority, the one chasing deadlines wants the two dates, and both
   want the other's columns out of the way. So it is a choice, and the choice
   is remembered — per browser, because it is a preference about how YOU read
   a table, not a fact about the team. Person and the document cell are not on
   the list: a row without them is not a row. */
const COLS = [
  { id: 'numero', fr: 'N°', en: 'No.' },
  { id: 'dossier', fr: 'Dossier', en: 'Dossier' },
  { id: 'type', fr: 'Type d’acte', en: 'Type' },
  { id: 'authority', fr: 'Autorité', en: 'Authority' },
  { id: 'status', fr: 'Statut', en: 'Status' },
  { id: 'emission', fr: 'Émission', en: 'Issued' },
  { id: 'expiration', fr: 'Expire', en: 'Expires' },
  { id: 'demande', fr: 'Demandée', en: 'Requested' },
  { id: 'reference', fr: 'Référence', en: 'Reference' },
  { id: 'owner', fr: 'Responsable', en: 'Owner' },
  { id: 'notes', fr: 'Notes', en: 'Notes' },
] as const;
type ColId = typeof COLS[number]['id'];
/* ── LES CINQ VUES RAPIDES DE LA MAQUETTE ──
   Les règles sont celles de la file de triage du tableau de bord, reprises
   telles quelles pour qu'un acte ne change pas d'humeur d'un écran à l'autre :
     · with   — parti, jamais revenu ;
     · exp    — reçu, et qui périme avant le dépôt (même fenêtre : 90 jours) ;
     · stuck  — sur un dossier, jamais demandé : il attend une décision ;
     · done   — en main.
   Rien n'est demandé au serveur pour ça : c'est un tri de ce qui est chargé. */
export type Seg = 'all' | 'with' | 'exp' | 'stuck' | 'done';
const SEGS: { k: Seg; fr: string; en: string }[] = [
  { k: 'all', fr: 'Tout', en: 'Everything' },
  { k: 'with', fr: 'Chez une autorité', en: 'With an authority' },
  { k: 'exp', fr: 'Expirent', en: 'Expiring' },
  { k: 'stuck', fr: 'À décider', en: 'Needs a decision' },
  { k: 'done', fr: 'En main', en: 'In hand' },
];
const dayNum = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00Z`).getTime();
  return Number.isNaN(d) ? null : Math.round((d - Date.now()) / 86_400_000);
};
function segOf(r: ActeRow): Seg {
  const got = !!r.is_received;
  if (got) {
    const left = dayNum(r.date_expiration);
    return left !== null && left <= 90 ? 'exp' : 'done';
  }
  return r.date_demande ? 'with' : 'stuck';
}

const COLS_KEY = 'lexidy.actes.demandes.cols';
const DEFAULT_COLS: ColId[] = ['numero', 'dossier', 'type', 'authority', 'status', 'emission', 'expiration', 'owner', 'notes'];

function readCols(): ColId[] {
  try {
    const raw = localStorage.getItem(COLS_KEY);
    if (!raw) return DEFAULT_COLS;
    const list = JSON.parse(raw) as string[];
    const kept = list.filter((c): c is ColId => COLS.some((x) => x.id === c));
    return kept.length ? kept : DEFAULT_COLS;
  } catch { return DEFAULT_COLS; }
}

export function DemandesPanel({ lang, reference, onOpenDossier, landing, openActe }: { lang: Lang; reference: ActesReference; onOpenDossier?: (id: string) => void; landing?: 'table' | 'scans'; openActe?: { id: string; n: number } | null }) {
  const [scope, setScope] = useState<Scope>('mine');
  /* Portugal reads types and statuses in English, and the request's own
     words where France names an authority. */
  const pt = countryOfRef(reference) === 'PT';
  /* Where the section was ASKED to land — the dashboard's "latest actes"
     means the scans, not the table. */
  const [view, setView] = useState<View>(landing ?? 'table');
  const [q, setQ] = useState('');
  const [find, setFind] = useState('');
  const [field, setField] = useState('person');
  const [statusId, setStatusId] = useState<number | 'all'>('all');
  const [typeId, setTypeId] = useState<number | 'all'>('all');
  const [assignee, setAssignee] = useState('');
  /* Qui est sur une demande : la principale ET celles d'à côté
     (`assigned_emails`) — 465 demandes en ont deux ou trois. */
  const who = useMemo(() => peopleReader(reference.profiles), [reference.profiles]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ActeRow | null>(null);
  const [picker, setPicker] = useState<ActeRow | null>(null);
  const [viewing, setViewing] = useState<ActeRow | null>(null);
  /* Asked for by a notification: open THAT demande's fiche, fetched by id
     (it may not be in the page of rows loaded, or not in «mine»). */
  useEffect(() => {
    if (!openActe?.id) return;
    let alive = true;
    void actesOne(openActe.id).then((r) => { if (alive && r) setViewing(r); }).catch(() => undefined);
    return () => { alive = false; };
  }, [openActe?.id, openActe?.n]);
  const [cols, setCols] = useState<ColId[]>(() => readCols());
  const [colsOpen, setColsOpen] = useState(false);
  const [seg, setSeg] = useState<Seg>('all');
  const on = (c: ColId) => cols.includes(c);
  const toggleCol = (c: ColId) => setCols((prev) => {
    const next = prev.includes(c) ? prev.filter((x) => x !== c) : [...COLS.map((x) => x.id)].filter((x) => prev.includes(x) || x === c);
    try { localStorage.setItem(COLS_KEY, JSON.stringify(next)); } catch { /* a preference that cannot be saved is still a preference */ }
    return next;
  });

  const rows = useQuery<ActeRow[]>({
    queryKey: ['actes', 'demandes', scope, reference.me.id],
    queryFn: ({ signal }) => actesDemandes(
      scope === 'none' ? { noDossier: true } : { assignedTo: reference.me.id },
      signal,
    ),
    enabled: scope !== 'all',
    staleTime: 60_000,
  });

  /* ── « TOUTES » SE LIT PAR PAGES ──
     There are some 4 700 requests. Asking for all of them was the slowest
     screen here and got slower every month. This walks them with a keyset
     cursor, 200 at a time, and says how many of how many you are looking at.
     The scoped views are untouched: they are bounded by what one person
     carries, and paging them would only add a button. */
  const [pages, setPages] = useState<ActeRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [more, setMore] = useState(false);
  const [paging, setPaging] = useState(false);
  useEffect(() => { setPages([]); setCursor(null); setTotal(0); setMore(false); }, [scope]);
  const loadPage = useCallback(async (from: string | null) => {
    setPaging(true);
    try {
      const r = await actesDemandesPage({ per: 200, before: from });
      setPages((prev) => (from ? [...prev, ...r.data] : r.data));
      setCursor(r.next);
      setMore(r.more);
      setTotal(r.total);
    } catch { /* the table keeps what it already has */ }
    setPaging(false);
  }, []);
  useEffect(() => { if (scope === 'all' && !pages.length && !paging) void loadPage(null); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scope]);

  const found = useQuery<ActeRow[]>({
    queryKey: ['actes', 'demandes', 'search', find, field],
    queryFn: ({ signal }) => actesSearch(find, field, undefined, signal),
    enabled: find.trim().length >= 2,
    staleTime: 30_000,
  });

  /* LES PERSONNES D'UNE DEMANDE, DEPUIS LES VISAGES DU TABLEAU.
     La liste entière part (la première est la principale). « Toutes » vit
     dans ses pages et pas dans une requête : la réponse y est recopiée, sans
     quoi le changement ne se voyait qu'au rechargement. */
  const [ownErr, setOwnErr] = useState('');
  const setOwners = async (id: string, emails: string[]) => {
    try {
      const { data } = await actesUpdateDemande(id, { assigned_emails: emails });
      if (data) {
        setPages((prev) => prev.map((x) => (x.id === id
          ? { ...x, assigned_to: data.assigned_to, assigned_emails: data.assigned_emails ?? emails, assignee_email: null, assignee_name: null }
          : x)));
      }
      setOwnErr('');
    } catch (e) { setOwnErr(friendlyError(e, lang)); }
    void rows.refetch();
    if (find.trim().length >= 2) void found.refetch();
  };

  /* CHERCHER, C'EST CHERCHER PARTOUT — ET L'ONGLET LE DIT.
     La recherche part au serveur sur les 4 700 demandes, quel que soit
     l'onglet : depuis « Les miennes » on voyait donc déjà des demandes qui
     ne sont pas à soi, pendant que la puce continuait d'afficher « Les
     miennes ». L'écran mentait. Dès qu'une recherche est lancée, l'onglet
     passe à « Toutes », qui est la vérité de ce qui s'affiche — et ce qui a
     été tapé reste tapé. */
  const jumped = useRef('');
  useEffect(() => {
    /* UNE SEULE FOIS PAR RECHERCHE, PAS À CHAQUE RENDU.
       En dépendant aussi de `scope`, l'effet ramenait sur « Toutes » chaque
       fois qu'on cliquait « Les miennes » : l'onglet devenait injouable tant
       qu'il restait du texte dans la case. On saute au moment où la recherche
       CHANGE ; après ça, l'onglet redevient un choix. */
    const q = find.trim();
    if (q.length < 2) { jumped.current = ''; return; }
    if (jumped.current === q) return;
    jumped.current = q;
    setScope((cur) => (cur === 'all' ? cur : 'all'));
  }, [find]);

  /* The paged scope reads its own pages; the others read their query. */
  const loaded = scope === 'all' ? pages : (rows.data ?? []);
  const base = find.trim().length >= 2 ? (found.data ?? []) : loaded;
  /* ⌘K en las demandes: persona, acte, dossier, estado, número, quién. */
  useEffect(() => publishScope({
    owner: 'actes-demandes',
    quiet: true,
    kind: 'tickets',
    hint: lang === 'fr' ? 'Aller à une demande — ou demander…' : 'Jump to a request — or ask…',
    items: loaded.map((r) => ({
      id: r.id,
      title: r.person_concerned,
      sub: [pt ? (r.request_line || typeLabelEn(reference.acteTypes, r.acte_type_id)) : (r.type_short_fr || r.type_label_fr), r.dossier_name].filter(Boolean).join(' · '),
      where: (pt ? reference.acteStatuses.find((s) => s.id === r.status_id)?.label_en : r.status_label_fr) || '',
      text: [r.person_concerned, r.type_label_fr, r.dossier_name, r.status_label_fr, r.numero, ...who.people(r).map(nameOf), r.authority_label_fr].filter(Boolean).join(' · '),
    })),
    onPick: (id) => { const r = loaded.find((x) => x.id === id); if (r) setEditing(r); },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [loaded, lang, who]);

  const shown = useMemo(
    () => base
      .filter((r) => (statusId === 'all' ? true : r.status_id === statusId))
      .filter((r) => (typeId === 'all' ? true : r.acte_type_id === typeId))
      .filter((r) => (assignee ? who.has(r, assignee) : true))
      .filter((r) => (seg === 'all' ? true : segOf(r) === seg)),
    [base, statusId, typeId, assignee, seg, who],
  );
  /* Le nombre sur chaque segment se compte AVANT le segment lui-même : sinon
     « Expirent 6 » deviendrait « Expirent 6 » puis « Expirent 0 » dès qu'on
     clique sur un autre. */
  const segCount = useMemo(() => {
    const pre = base
      .filter((r) => (statusId === 'all' ? true : r.status_id === statusId))
      .filter((r) => (typeId === 'all' ? true : r.acte_type_id === typeId))
      .filter((r) => (assignee ? who.has(r, assignee) : true));
    const out: Record<Seg, number> = { all: pre.length, with: 0, exp: 0, stuck: 0, done: 0 };
    for (const r of pre) out[segOf(r)] += 1;
    return out;
  }, [base, statusId, typeId, assignee, who]);

  /* The badge on each row, asked once for the page that is actually on
     screen — not for the 4 700 that are not. */
  const visibleIds = useMemo(() => shown.slice(0, 500).map((r) => r.id), [shown]);
  const counts = useQuery<Record<string, number>>({
    queryKey: ['actes', 'doc-counts', visibleIds.join(',').slice(0, 200), visibleIds.length],
    queryFn: ({ signal }) => actesDocumentCounts(visibleIds, signal),
    enabled: visibleIds.length > 0,
    staleTime: 60_000,
  });
  const docCounts = counts.data ?? {};
  /* The 💬 on a row: how many notes the team has left on that demande. Asked
     once for what is on screen, like the document badge. */
  const noteQ = useQuery<Record<string, number>>({
    queryKey: ['actes', 'note-counts', visibleIds.join(',').slice(0, 200), visibleIds.length],
    queryFn: ({ signal }) => actesCommentCounts(visibleIds, signal),
    enabled: visibleIds.length > 0,
    staleTime: 60_000,
  });
  const noteCounts = noteQ.data ?? {};

  /* ── LOS SEGMENTOS DICEN CUÁNTOS (propuesta de la maqueta v7) ──
     Un segmento sin cifra obliga a hacer clic para saber si hay algo detrás.
     Aquí lleva el número — pero SÓLO cuando ya se conoce de verdad: el que se
     ha cargado. No se inventa una cifra ni se pide una consulta más por
     adornar una pestaña; en cuanto se visita un ámbito, su número se queda.
     `all` usa el total real que devuelve la paginación, no las 200 en pantalla. */
  const [seen, setSeen] = useState<Partial<Record<Scope, number>>>({});
  useEffect(() => {
    if (scope === 'all') { if (total) setSeen((s) => (s.all === total ? s : { ...s, all: total })); return; }
    if (rows.isSuccess && rows.data) {
      const n = rows.data.length;
      setSeen((s) => (s[scope] === n ? s : { ...s, [scope]: n }));
    }
  }, [scope, total, rows.isSuccess, rows.data]);
  const nOf = (s: Scope) => (seen[s] === undefined ? null : <span className="n">{seen[s]}</span>);

  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  /* ── LA LIGNE QU'ON VIENT DE CRÉER ──
     A new request is one row among four hundred and looks like all of them.
     The one just made is scrolled to and flashed once, then forgotten — a
     later visit must not celebrate yesterday's work. */
  /* ── LA DEMANDE QU'ON NOUS A DEMANDÉ D'OUVRIR ──
     Le tableau de bord ne peut pas ouvrir une fiche : il change d'onglet.
     Il nomme donc la demande, et c'est ici qu'on l'ouvre — depuis la table si
     elle y est déjà, sinon en allant la chercher par son id. */
  const wanted = useSyncExternalStore(onActeOpen, takeActeOpen, () => null);
  useEffect(() => {
    if (!wanted) return;
    const hit = loaded.find((r) => r.id === wanted) ?? (found.data ?? []).find((r) => r.id === wanted);
    if (hit) { setViewing(hit); return; }
    void actesOne(wanted).then((r) => setViewing(r)).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanted]);

  const freshId = useSyncExternalStore(onNewActe, newActe, newActe);
  const freshRow = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    if (!freshId || !freshRow.current) return;
    freshRow.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const t = window.setTimeout(clearNewActe, 2600);
    return () => window.clearTimeout(t);
  }, [freshId]);
  const fmt = (d: unknown) => fmtDay(d, lang, { day: '2-digit', month: 'short' });
  /* ── LA CELLULE « ÉMISSION → EXPIRE » DE LA MAQUETTE ──
     Deux dates, ce qui reste, et dessous une barre qui montre la part de vie
     encore devant l'acte. La part n'est pas une invention : c'est la fraction
     entre l'émission et l'expiration, deux dates que la demande porte déjà.
     Sans expiration il n'y a pas de barre, et on le dit. */
  const daysN = (iso: string | null | undefined): number | null => {
    if (!iso) return null;
    const d = new Date(`${iso}T00:00:00Z`).getTime();
    return Number.isNaN(d) ? null : Math.round((d - Date.now()) / 86_400_000);
  };
  const leftWord = (n: number): string => {
    if (n < 0) return T(`échu · ${-n} j`, `expired · ${-n} d`);
    if (n <= 13) return T(`${n} j`, `${n} d`);
    if (n <= 70) return T(`${Math.round(n / 7)} sem.`, `${Math.round(n / 7)} weeks`);
    return T(`${Math.round(n / 30)} mois`, `${Math.round(n / 30)} months`);
  };
  const lifeCell = (r: ActeRow) => {
    if (!r.date_expiration) return <span className="dash">{T('pas encore délivré', 'not issued yet')}</span>;
    const n = daysN(r.date_expiration) ?? 0;
    const iss = daysN(r.date_emission);
    const span = iss !== null ? n - iss : null;
    const pct = span && span > 0 ? Math.max(0, Math.min(100, Math.round((n / span) * 100))) : null;
    const cls = pct === null ? '' : pct < 50 ? 'now' : pct < 70 ? 'soon' : '';
    return (
      <div className="val">
        <div className="t">
          <b>{fmt(r.date_emission)}</b><span className="arw">→</span><b>{fmt(r.date_expiration)}</b>
          <span className={`rem ${n <= 42 ? 'late' : ''}`}>{leftWord(n)}</span>
        </div>
        {pct !== null && <div className="vbar"><i className={cls} style={{ width: `${pct}%` }} /></div>}
      </div>
    );
  };

  return (
    <section className="actes-panel v7">
      {/* Même première ligne que partout ailleurs : le titre, et l'action. */}
      <div className="phead">
        <h2>{T('Demandes d’actes', 'Civil-record requests')}</h2>
        {reference.me.isStaff && view === 'table' && (
          <div className="right">
            <button className="btn btn-p" onClick={() => setCreating(true)}>
              <KBIcon name="plus" size={13} /> {T('Nouvelle demande', 'New request')}
            </button>
          </div>
        )}
      </div>

      <div className="pillrow">
        <button className="fp" aria-pressed={view === 'table' && scope === 'mine'}
          onClick={() => { setView('table'); setScope('mine'); }}>{T('Les miennes', 'Mine')}{nOf('mine')}</button>
        <button className="fp" aria-pressed={view === 'table' && scope === 'all'}
          onClick={() => { setView('table'); setScope('all'); }}>{T('Toutes', 'All')}{nOf('all')}</button>
        {/* In Portugal every request lives in a dossier: no «no dossier» pile. */}
        {!pt && (
          <button className="fp" aria-pressed={view === 'table' && scope === 'none'}
            onClick={() => { setView('table'); setScope('none'); }}>{T('Sans dossier', 'No dossier')}{nOf('none')}</button>
        )}
        <button className="fp" aria-pressed={view === 'scans'} onClick={() => setView('scans')}>
          <KBIcon name="ai" size={12} /> {T('Derniers scans', 'Latest scans')}
        </button>
      </div>

      {view === 'scans' && (
        <ScansList lang={lang} onOpenDemande={(acteId) => {
          /* Coming back from a scan: the table is where the fiche lives, so
             switch to it and open the demande the scan was filed on. Asked
             for by id because the scan knows the id, not the row. */
          setView('table'); setScope('all'); setFind(''); setQ('');
          const hit = loaded.find((r) => r.id === acteId);
          if (hit) setViewing(hit);
          else void actesOne(acteId).then((r) => setViewing(r)).catch(() => undefined);
        }} />
      )}

      {view === 'table' && (
      <>

      {/* The search line, then the filters — the shape the team already works
          with. The search goes to the SERVER, so it reads all 4 700 demandes
          and not the few hundred this table happens to hold. */}
      {/* LA BARRE DE LA MAQUETTE : le « chercher par » vit DANS le champ de
          recherche, séparé par un filet, et la ligne d'aide passe dessous en
          pleine largeur. Ce que fait la recherche n'a pas changé d'un cheveu :
          elle part sur Entrée ou sur le bouton, et elle lit les 4 700 demandes
          au serveur, pas les quelques centaines à l'écran. */}
      <div className="toolbar">
        <label className="srch">
          <select className="plain-sel" aria-label={T('Chercher par', 'Search by')}
            value={field} onChange={(e) => { setField(e.target.value); setQ(''); setFind(''); }}>
            <option value="person">{T('Personne concernée', 'Person concerned')}</option>
            <option value="dossier">{T('Dossier', 'Dossier')}</option>
            <option value="reference">{T('Référence', 'Reference')}</option>
            <option value="authority">{T('Autorité', 'Authority')}</option>
          </select>
          <span className="vr" />
          <KBIcon name="search" size={15} />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); if (!e.target.value.trim()) setFind(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') setFind(q); }}
            aria-label={T('Terme de recherche', 'Search term')}
            placeholder={
              field === 'dossier' ? T('Nom du dossier…', 'Dossier name…')
              : field === 'reference' ? T('Référence de la demande…', 'Request reference…')
              : field === 'authority' ? T('Mairie, SCEC, archives…', 'Town hall, SCEC, archives…')
              : T('Nom de la personne concernée…', 'Name of the person concerned…')
            }
          />
          {(q || find) && <button className="x" onClick={() => { setQ(''); setFind(''); }} aria-label="clear">×</button>}
        </label>
        <button className="btn btn-s" disabled={q.trim().length < 2} onClick={() => setFind(q)}>
          {T('Chercher', 'Search')}
        </button>
        {/* « J'AI TAPÉ ET IL NE SE PASSE RIEN. » Tant que ce qui est écrit n'a
            pas été cherché, la ligne le dit au lieu d'expliquer la recherche. */}
        <span className={`hint fullw ${q.trim().length >= 2 && q !== find ? 'act' : ''}`}>
          {q.trim().length >= 2 && q !== find
            ? T('Appuyez sur Entrée pour chercher.', 'Press Enter to search.')
            : find
              ? T('Résultats sur TOUTES les demandes, quel que soit le responsable.',
                  'Results across ALL requests, whoever owns them.')
              : T('La recherche parcourt toutes les demandes, pas seulement celles affichées.',
                  'The search reads every request, not only the ones on screen.')}
        </span>
        {/* Demander À QUI pendant que l'onglet dit déjà « les miennes », c'est
            poser deux fois la même question. */}
        {scope !== 'mine' && (
          <label className="hint">
            {T('Responsable', 'Owner')}
            <select className="sel" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
              <option value="">{T('Tous', 'All')}</option>
              {reference.profiles.map((p) => (
                <option key={p.id} value={p.id}>{p.full_name ?? p.email}</option>
              ))}
            </select>
          </label>
        )}
        <label className="hint">
          {T('Statut', 'Status')}
          <select className="sel" value={statusId} onChange={(e) => setStatusId(e.target.value !== 'all' ? Number(e.target.value) : 'all')}>
            <option value="all">{T('Tous', 'All')}</option>
            {reference.acteStatuses.map((s) => (
              <option key={s.id} value={s.id}>{lang === 'fr' ? s.label_fr : s.label_en}</option>
            ))}
          </select>
        </label>
        <label className="hint">
          {T('Type d’acte', 'Type')}
          <select className="sel" value={typeId} onChange={(e) => setTypeId(e.target.value !== 'all' ? Number(e.target.value) : 'all')}>
            <option value="all">{T('Tous', 'All')}</option>
            {reference.acteTypes.filter((t) => t.is_active).map((t) => (
              <option key={t.id} value={t.id}>{lang === 'fr' ? t.label_fr : t.label_en}</option>
            ))}
          </select>
        </label>
        {(assignee || statusId !== 'all' || typeId !== 'all' || find) && (
          <button className="rb" title={T('Effacer les filtres', 'Clear the filters')}
            onClick={() => { setAssignee(''); setStatusId('all'); setTypeId('all'); setQ(''); setFind(''); }}>
            <KBIcon name="trash" size={15} />
          </button>
        )}
        {/* Quelles colonnes VOUS lisez ce tableau avec. Retenu par navigateur. */}
        <div className="actes-cols">
          <button className="rb" aria-expanded={colsOpen} title={T('Colonnes', 'Columns')}
            onClick={() => setColsOpen((v) => !v)}>
            <KBIcon name="sliders" size={15} />
          </button>
          {colsOpen && (
            <>
              <div className="actes-cols-scrim" onClick={() => setColsOpen(false)} />
              <div className="actes-cols-pop" role="dialog">
                <div className="cap">{T('Colonnes', 'Columns')}</div>
                {COLS.map((c) => (
                  <label key={c.id} className="checkline">
                    <input type="checkbox" checked={on(c.id)} onChange={() => toggleCol(c.id)} />
                    {T(c.fr, c.en)}
                  </label>
                ))}
                <button className="btn btn-s" onClick={() => {
                  setCols([...DEFAULT_COLS]);
                  try { localStorage.setItem(COLS_KEY, JSON.stringify(DEFAULT_COLS)); } catch { /* ignore */ }
                }}>{T('Par défaut', 'Default')}</button>
              </div>
            </>
          )}
        </div>
        <span className="res mono">
          {scope === 'all' && total
            ? `${shown.length} / ${total} ${T('résultats', 'results')}`
            : `${shown.length} ${T('résultats', 'results')}`}
        </span>
      </div>

      {/* ── LES SEGMENTS COMPTÉS (proposition de la maquette v7) ──
          Cinq vues rapides au-dessus du tableau, chacune avec son nombre. Les
          règles ne sont pas inventées pour l'occasion : ce sont EXACTEMENT
          celles de la file de triage du tableau de bord, pour qu'un même acte
          ne soit pas « en retard » ici et « à l'heure » là-bas. Le compte est
          celui des lignes chargées, et rien n'est demandé de plus. */}
      <div className="pillrow" style={{ marginTop: -6 }}>
        <div className="seg" role="group" aria-label={T('Vues rapides', 'Quick views')}>
          {SEGS.map((sg) => (
            <button key={sg.k} aria-pressed={seg === sg.k} onClick={() => setSeg(sg.k)}>
              {T(sg.fr, sg.en)} <span className="n">{segCount[sg.k]}</span>
            </button>
          ))}
        </div>
      </div>

      {rows.isLoading || found.isLoading ? (
        <p className="muted-note">{T('Chargement…', 'Loading…')}</p>
      ) : shown.length === 0 ? (
        <div className="empty-state">
          <div className="ico">🗂️</div>
          <h3>{T('Aucune demande', 'No requests')}</h3>
          <p>{scope !== 'all'
            ? T('Rien ici — mais vous ne regardez qu’une partie des demandes.',
                'Nothing here — but you are only looking at part of the requests.')
            : T('Rien ne correspond à ce filtre.', 'Nothing matches this filter.')}</p>
          {/* LA SORTIE DE SON PROPRE ESPACE.
              Chercher un dossier ou une demande qui n'est pas à soi depuis
              « Les miennes » ne rendait rien et s'arrêtait là. Le bouton
              reprend la même recherche sur tout. */}
          {scope !== 'all' && (
            <button className="btn btn-sm" onClick={() => { setScope('all'); if (q.trim().length >= 2) setFind(q); }}>
              <KBIcon name="search" size={13} /> {T('Chercher dans toutes les demandes', 'Search across all requests')}
            </button>
          )}
          {/* « Effacer les filtres » n'est une sortie que s'il Y A des filtres.
              Ce qui a été tapé n'en est pas un et ne doit jamais être effacé
              par ce bouton : on élargit, on ne recommence pas. */}
          {scope === 'all' && (assignee || statusId !== 'all' || typeId !== 'all') && (
            <button className="btn btn-sm btn-ghost"
              onClick={() => { setAssignee(''); setStatusId('all'); setTypeId('all'); }}>
              {T('Élargir : enlever les filtres', 'Widen: drop the filters')}
            </button>
          )}
        </div>
      ) : (
        <>
        {ownErr && <p className="muted-note ni-err">⚠ {ownErr}</p>}
        <div className="tbl">
          <table>
            <thead>
              <tr>
                {/* Le filet muet : il dit si la ligne presse, sans un mot. */}
                <th className="cs" />
                <th style={{ width: 44 }}>{T('Doc', 'Doc')}</th>
                {on('numero') && <th style={{ width: 40 }}>#</th>}
                <th>{T('Personne concernée', 'Person concerned')}</th>
                {on('dossier') && <th className="hide-s">{T('Dossier', 'Dossier')}</th>}
                {on('type') && <th>{T('Type', 'Type')}</th>}
                {on('authority') && <th className="hide-s">{pt ? 'Document requested' : T('Autorité', 'Authority')}</th>}
                {on('status') && <th>{T('Statut', 'Status')}</th>}
                {on('emission') && <th>{T('Émission → expire', 'Issued → expires')}</th>}
                {on('demande') && <th className="hide-s">{T('Demandée', 'Requested')}</th>}
                {on('reference') && <th className="hide-s">{T('Référence', 'Reference')}</th>}
                {on('owner') && <th>{T('Responsable', 'Owner')}</th>}
                {on('notes') && <th style={{ width: 40 }} title={T('Notes', 'Notes')}><KBIcon name="chat" size={13} /></th>}
                <th className="r" />
              </tr>
            </thead>
            <tbody>
              {shown.slice(0, 500).map((r) => {
                const docs = (r.file_path ? 1 : 0) + (docCounts[r.id] ?? 0);
                const left = daysN(r.date_expiration);
                const out = daysN(r.date_demande);
                /* now / soon / rien : le même barème que le tableau de bord. */
                const urg = !r.is_received && out !== null && out <= -45 ? 'now'
                  : r.is_received && left !== null && left <= 42 ? 'now'
                    : !r.is_received && out !== null && out <= -21 ? 'soon'
                      : r.is_received && left !== null && left <= 90 ? 'soon' : '';
                /* L'âge sur la pastille : depuis que la demande est partie.
                   Pas de date de départ, pas d'âge — on n'en fabrique pas. */
                const age = out === null ? null : T(`${-out} j`, `${-out} d`);
                return (
                  <tr key={r.id} ref={r.id === freshId ? freshRow : undefined}
                    className={r.id === freshId ? 'actes-fresh' : undefined}>
                    <td className="cs"><i className={urg} /></td>
                    <td>
                      <button type="button" className={`docbtn ${docs ? '' : 'off'}`}
                        title={docs ? T('Voir le document', 'Open the document') : T('Aucun document', 'No document yet')}
                        onClick={() => setPicker(r)}>
                        {docs > 1 && <span className="ct">{docs}</span>}
                        <KBIcon name="documents" size={13} />
                      </button>
                    </td>
                    {on('numero') && <td className="mono" style={{ color: 'var(--muted-2)' }}>{r.numero || '—'}</td>}
                    <td className="nm-c">
                      <b>{r.person_concerned}</b>
                      {!on('reference') && r.reference && <span className="chipref">{r.reference}</span>}
                    </td>
                    {on('dossier') && (
                      <td className="hide-s">{r.dossier_id && onOpenDossier
                        ? <button type="button" className="lnk" onClick={() => onOpenDossier(r.dossier_id as string)}>{r.dossier_name || '—'}</button>
                        : <button type="button" className="attachbtn" onClick={() => setEditing(r)}>+ {T('dossier', 'dossier')}</button>}</td>
                    )}
                    {on('type') && <td>{(pt ? typeLabelEn(reference.acteTypes, r.acte_type_id) : r.type_short_fr || r.type_label_fr) || '—'}</td>}
                    {on('authority') && (
                      <td className="hide-s sub">{(pt ? r.request_line : r.authority_label_fr) || <span className="dash">{pt ? '—' : T('pas choisie', 'not chosen')}</span>}</td>
                    )}
                    {on('status') && (
                      <td>
                        <span className={`pill ${pillOf(r.status_color)}`}>
                          {pt ? (reference.acteStatuses.find((s) => s.id === r.status_id)?.label_en ?? r.status_label_fr) : r.status_label_fr}{age && <span className="age">· {age}</span>}
                        </span>
                      </td>
                    )}
                    {on('emission') && <td>{lifeCell(r)}</td>}
                    {on('demande') && <td className="hide-s sub">{fmt(r.date_demande)}</td>}
                    {on('reference') && <td className="hide-s sub">{r.reference || '—'}</td>}
                    {on('owner') && (
                      <td>
                        <span className="own">
                          <OwnerPicker lang={lang === 'fr' ? 'fr' : 'en'} people={reference.profiles}
                            value={who.people(r)} canEdit={reference.me.isStaff}
                            onChange={(emails) => { void setOwners(r.id, emails); }} />
                        </span>
                      </td>
                    )}
                    {on('notes') && (
                      <td>
                        {noteCounts[r.id]
                          ? <button type="button" className="notes-c" title={T('Notes de l’équipe', 'Team notes')} onClick={() => setViewing(r)}>
                              <KBIcon name="chat" size={13} /> {noteCounts[r.id]}
                            </button>
                          : <span className="dash">—</span>}
                      </td>
                    )}
                    <td className="r">
                      <div className="rowbtns">
                        <button type="button" className="rb" title={T('Voir la demande', 'Open')} onClick={() => setViewing(r)}>
                          <KBIcon name="eye" size={13} />
                        </button>
                        <button type="button" className="rb" title={T('Modifier', 'Edit')} onClick={() => setEditing(r)}>
                          <KBIcon name="edit" size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* Une page de plus, seulement là où il y en a une. */}
          {scope === 'all' && more && find.trim().length < 2 && (
            <div className="cardfoot">
              <button className="btn btn-s" disabled={paging} onClick={() => void loadPage(cursor)}>
                {paging
                  ? T('Chargement…', 'Loading…')
                  : T(`Charger 200 de plus (${total - pages.length} restantes)`, `Load 200 more (${total - pages.length} left)`)}
              </button>
            </div>
          )}
          {shown.length > 500 && (
            <p className="muted-note">{T(`… et ${shown.length - 500} autres — affinez la recherche.`, `… and ${shown.length - 500} more — narrow the search.`)}</p>
          )}
        </div>
        </>
      )}
      </>
      )}

      {creating && (
        <ActeModal lang={lang} reference={reference} onClose={() => setCreating(false)} />
      )}
      {editing && (
        <ActeModal lang={lang} reference={reference} initial={editing} onClose={() => setEditing(null)} />
      )}
      {viewing && (
        <ActeView lang={lang} reference={reference} row={viewing} onOpenDossier={onOpenDossier}
          onClose={() => setViewing(null)}
          onEdit={() => { const r = viewing; setViewing(null); setEditing(r); }} />
      )}
      {picker && (
        <DocumentPicker lang={lang} acteId={picker.id} person={picker.person_concerned}
          hasReceived={!!picker.file_path} receivedName={picker.file_name} onClose={() => setPicker(null)} />
      )}
    </section>
  );
}
